import React from "react"
import { useTranslation } from "react-i18next"
import { useAtomValue } from "jotai"
import { CandidateBadgeIcon, KeeperIcon, RejectBadgeIcon } from "@/components/icons/CullingIcon"
import { Select, SelectContent, SelectItem } from "@/components/ui/Select"
import { SelectTrigger } from "@radix-ui/react-select"
import { PhotoRecord } from "@/lib/catalog"
import { CullingTag, CullingValues } from "@/utils/exif"
import {
	isCullingTagOptionEnabled,
	isShowingRejectsAtom,
	isShowingRejectsOrKeepersAtom,
	isSuggestingKeepersAtom,
	setManualCullingTagForPhotos,
} from "@/store/importing/culling-state"
import { cullingTagKeys } from "@/i18n/dual-role-strings"

export const CullingTagIcon = (props: { tag: CullingTag; size: number }) => {
	switch (props.tag) {
		case "Keeper":
			return <KeeperIcon size={props.size} />
		case "Candidate":
			return <CandidateBadgeIcon size={props.size} />
		case "Reject":
			return <RejectBadgeIcon size={props.size} />
	}
	return null
}

export const CullingTagSetter: React.FC<{
	photo: PhotoRecord
	tag: CullingTag
	size: number
	children?: React.ReactNode
	contentSide?: React.ComponentPropsWithoutRef<typeof SelectContent>["side"]
	contentAlign?: React.ComponentPropsWithoutRef<typeof SelectContent>["align"]
	onOpenChange?: (open: boolean) => void
	trackingLabel?: string
	triggerClassName?: string
}> = ({
	photo,
	tag,
	size,
	children,
	contentSide = "top",
	contentAlign = "end",
	onOpenChange,
	trackingLabel = "thumbnail_culling_tag_setter",
	triggerClassName,
}) => {
	const { t } = useTranslation("components")
	const isShowingKeepers = useAtomValue(isSuggestingKeepersAtom)
	const isShowingRejects = useAtomValue(isShowingRejectsAtom)
	const isShowingRejectsOrKeepers = useAtomValue(isShowingRejectsOrKeepersAtom)
	const currentValue = photo.manualCullingTag ?? "Auto"
	const [open, setOpen] = React.useState(false)
	const suppressNextOpenRef = React.useRef(false)

	const isCullingTagAvailable = (cullingTag: CullingTag | null): boolean =>
		isCullingTagOptionEnabled({
			tag: cullingTag,
			isShowingRejectsOrKeepers,
			isShowingKeepers,
			isShowingRejects,
		})

	const handleOpenChange = (nextOpen: boolean) => {
		if (nextOpen && suppressNextOpenRef.current) {
			suppressNextOpenRef.current = false
			setOpen(false)
			onOpenChange?.(false)
			return
		}

		setOpen(nextOpen)
		onOpenChange?.(nextOpen)
	}

	return (
		<Select
			trackingLabel={trackingLabel}
			aria-label={t("cullingTagSetter.ariaSetPhotoCullingTag")}
			open={open}
			value={currentValue}
			onValueChange={(value) => {
				const nextTag: CullingTag | null =
					value === "Keeper" || value === "Candidate" || value === "Reject" ? value : null
				void setManualCullingTagForPhotos([photo.id], nextTag)
			}}
			onOpenChange={handleOpenChange}
		>
			<SelectTrigger
				className={triggerClassName}
				onKeyDown={(event) => {
					if (event.key.startsWith("Arrow")) {
						suppressNextOpenRef.current = true
					}
				}}
				onPointerDown={(event) => {
					suppressNextOpenRef.current = false
					event.stopPropagation()
				}}
				onClick={(event) => event.stopPropagation()}
			>
				{children ?? (
					<div className="cursor-pointer">
						<CullingTagIcon tag={tag} size={size} />
					</div>
				)}
			</SelectTrigger>
			<SelectContent side={contentSide} align={contentAlign}>
				<SelectItem value="Auto" disabled={!isCullingTagAvailable(null)}>
					<div className="text-xs">{t("cullingTagSetter.auto")}</div>
				</SelectItem>
				{CullingValues.map((option) => (
					<SelectItem
						key={option}
						value={option}
						disabled={!isCullingTagAvailable(option)}
					>
						<div className="text-xs">{t(cullingTagKeys[option])}</div>
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	)
}

const CullingTagIconSetter: React.FC<{
	photo: PhotoRecord
	tag: CullingTag
	size: number
}> = ({ photo, tag, size }) => {
	return (
		<CullingTagSetter photo={photo} tag={tag} size={size}>
			<div className="cursor-pointer">
				<CullingTagIcon tag={tag} size={size} />
			</div>
		</CullingTagSetter>
	)
}

export const MemoizedCullingTagIconSetter = React.memo(
	CullingTagIconSetter,
	(prev, next) =>
		prev.photo.id === next.photo.id &&
		prev.photo.manualCullingTag === next.photo.manualCullingTag &&
		prev.tag === next.tag &&
		prev.size === next.size,
)
